import React, { useCallback, useMemo, useState } from "react";
import { Button } from "reactstrap";
import { GOOGLE_PROJECTS_SIDE_MODAL } from "../../../../constants/modal.constant";
import { GOOGLE_GET_LOADING } from "../../../../constants/loading.constant";
import { useGlobalCore } from "../../../../cores/globals";
import { useGoogleCore } from "../../../../cores/google";
import { GoogleProject } from "../../../../cores/google/interface/api/get-google-projects";
import { useMeCore } from "../../../../cores/me";
import { GooglenAnalyticsIcon } from "./GoogleAnalitycsIcon";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TooltipWembii } from "../../TooltipWembii";

export default function GoogleProjects() {
  const { projects, googleEmail, accountCount } = useGoogleCore();
  const { isLoading } = useGlobalCore();
  const loading = isLoading(GOOGLE_GET_LOADING);
  const list = projects ?? [];

  return (
    <div className="p-4 overflow-auto">
      <h2 className="h2 text-center mb-4">Elige una propiedad GA4</h2>
      {loading && (
        <div className="h6 py-3 text-center mt-3">
          Cargando propiedades de Google Analytics…
        </div>
      )}
      {!loading && list.length === 0 && (
        <div className="h6 py-3 text-center wembii-border mt-3 px-3">
          <p className="mb-2">
            No hay propiedades GA4 disponibles
            {googleEmail ? (
              <>
                {" "}
                para <strong>{googleEmail}</strong>
              </>
            ) : (
              " para esta cuenta de Google"
            )}
            .
          </p>
          <p className="mb-2 small text-muted">
            {accountCount && accountCount > 0
              ? `Se encontró acceso a ${accountCount} cuenta(s) de Analytics, pero sin propiedades GA4 visibles.`
              : "Esta cuenta de Google no tiene acceso a ninguna cuenta/propiedad de Analytics."}
          </p>
          <p className="mb-0 small">
            Entrá a Google Analytics con esa misma cuenta, confirmá que exista al
            menos una propiedad GA4, y volvé a vincular. Si la propiedad está en
            otra cuenta de Google, usá esa cuenta al autorizar.
          </p>
        </div>
      )}
      {!loading &&
        list.map((project) => (
          <GoogleProjectComponent key={project.id} project={project} />
        ))}
    </div>
  );
}

const GoogleProjectComponent = ({ project }: { project: GoogleProject }) => {
  const { name, webProperties } = project;
  const { tokens, bind } = useGoogleCore();
  const { company, getMeSocialSettings } = useMeCore();
  const { turnOffModal } = useGlobalCore();
  const [reason, setReason] = useState("");
  const property = webProperties?.[0];
  const disabled = useMemo(() => {
    if (!tokens?.token || !tokens?.refreshToken) {
      setReason("token");
    }
    if (!company) {
      setReason("company");
    }

    if (!property) {
      setReason("web-properties");
    }
    return (
      !tokens?.token || !tokens?.refreshToken || !company || !property
    );
  }, [company, tokens, property]);

  const handleClick = useCallback(() => {
    if (tokens && tokens.refreshToken && tokens.token && project.id)
      bind(
        company!.id,
        tokens?.token!,
        tokens?.refreshToken!,
        project.id,
        () => {
          getMeSocialSettings(company?.id!);
        }
      );
    turnOffModal(GOOGLE_PROJECTS_SIDE_MODAL);
  }, [bind, company, project.id, tokens, turnOffModal, getMeSocialSettings]);

  return (
    <div className="align-items-center d-flex p-2 px-3 neu-in pointer mb-2 justify-content-between">
      <GooglenAnalyticsIcon active />
      <span className="h5 mb-0 ml-3 w-100"> {name} </span>
      {property?.websiteUrl ? (
        <span className="small mb-0 ml-3">
          <a href={property.websiteUrl} target="_blank" rel="noreferrer">
            {property.websiteUrl}
          </a>
        </span>
      ) : null}
      <TooltipWembii
        id={`google-project-${project.id}`}
        text={`${
          reason === "token"
            ? "No se puede sincronizar este sitio"
            : reason === "company"
            ? "No existe la companía"
            : "La propiedad GA4 no está disponible para sincronizar"
        } `}
      />
      <div className="d-flex my-auto mx-auto ">
        {disabled ? (
          <>
            <FontAwesomeIcon
              icon={["fas", "info-circle"]}
              size="1x"
              id={`google-project-${project.id}`}
              color="grey"
            />
          </>
        ) : (
          <></>
        )}
        <Button
          className="ml-auto"
          color="primary"
          onClick={handleClick}
          disabled={disabled}
        >
          Vincular
        </Button>
      </div>
    </div>
  );
};
